import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Trash2, RefreshCw, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

const ScreenshotPicker = ({ file, onChange, onError }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) return onError("Formato no permitido. Usa PNG, JPG o WEBP.");
    if (f.size > MAX_BYTES) return onError("La imagen supera el tamaño máximo de 5 MB.");
    onError(null);
    onChange(f);
  };

  return (
    <div>
      <p className="text-sm font-semibold text-white/90 mb-2">Adjuntar captura de pantalla <span className="text-brand-muted font-normal">(opcional · PNG, JPG o WEBP · máx. 5 MB)</span></p>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="support-screenshot-input" onChange={pick} />
      {file ? (
        <div data-testid="support-screenshot-preview" className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-2.5">
          <img src={preview} alt="Captura adjunta" className="w-20 h-14 object-cover rounded-lg border border-white/10 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{file.name}</p>
            <p className="text-xs text-brand-muted">{(file.size / 1024).toFixed(0)} KB</p>
          </div>
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="support-screenshot-replace" className="p-2 rounded-lg text-cyan-300 hover:bg-white/10 transition-colors" aria-label="Reemplazar imagen"><RefreshCw className="w-4 h-4" /></button>
          <button type="button" onClick={() => onChange(null)} data-testid="support-screenshot-remove" className="p-2 rounded-lg text-[#FF5A5A] hover:bg-white/10 transition-colors" aria-label="Eliminar imagen"><Trash2 className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} data-testid="support-screenshot-add" className="vt-btn-secondary w-full sm:w-auto text-sm border-dashed">
          <ImagePlus className="w-4 h-4 text-purple-300" /> + Añadir imagen
        </button>
      )}
    </div>
  );
};

export default function SupportForm() {
  const { user } = useAuth();
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);

  const canSend = description.trim().length >= 10 && (user || /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim())) && !sending;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    setError(null);
    const fd = new FormData();
    fd.append("description", description.trim());
    if (!user) fd.append("email", email.trim());
    fd.append("page", window.location.pathname + window.location.search);
    fd.append("user_agent", navigator.userAgent);
    if (file) fd.append("screenshot", file);
    try {
      const r = await axios.post(`${API}/support/report`, fd);
      setSuccess(r.data.message);
      setDescription(""); setEmail(""); setFile(null);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo enviar el reporte. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <div data-testid="support-success" className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-6 text-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
        <p className="font-semibold text-emerald-100">{success}</p>
        <button type="button" onClick={() => setSuccess(null)} data-testid="support-new-report" className="vt-btn-secondary text-sm mt-5">Enviar otro reporte</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="support-form" className="space-y-5">
      <div>
        <label htmlFor="support-description" className="block text-sm font-semibold text-white/90 mb-2">Descripción del problema</label>
        <textarea
          id="support-description" data-testid="support-description" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Cuéntanos qué ocurrió..." maxLength={3000} rows={5} required
          className="vt-input resize-none min-h-[120px] text-sm"
        />
        <p className="text-xs text-brand-muted mt-1 text-right">{description.length}/3000</p>
      </div>

      <ScreenshotPicker file={file} onChange={setFile} onError={setError} />

      {user ? (
        <p data-testid="support-user-email" className="text-xs text-brand-text2">Te responderemos a <span className="font-semibold text-white">{user.email}</span></p>
      ) : (
        <div>
          <label htmlFor="support-email" className="block text-sm font-semibold text-white/90 mb-2">Tu correo electrónico</label>
          <input id="support-email" data-testid="support-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" required className="vt-input text-sm" />
        </div>
      )}

      {error && (
        <div data-testid="support-error" className="vt-alert vt-alert-error flex items-center gap-2"><AlertCircle className="w-4 h-4 shrink-0" />{error}</div>
      )}

      <button type="submit" disabled={!canSend} data-testid="support-submit" className="vt-btn-primary w-full">
        {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4" /> Enviar reporte</>}
      </button>
    </form>
  );
}
